import React from 'react';
import './Customization.css';

export type BoulderCustomization = 'default' | 'blue' | 'green' | 'red' | 'yellow' | 'shiny' | 'rainbow' | 'alien' | 'pizza';

interface CustomizationItem {
  name: string;
  emoji: string;
  cost: number;
  description: string;
}

export const CUSTOMIZATIONS: Record<BoulderCustomization, CustomizationItem> = {
  default: {
    name: 'Default',
    emoji: '🪨',
    cost: 0,
    description: 'The classic gray boulder',
  },
  blue: {
    name: 'Blue',
    emoji: '🔵',
    cost: 50,
    description: 'A cool blue boulder',
  },
  green: {
    name: 'Green',
    emoji: '🟢',
    cost: 50,
    description: 'A fresh green boulder',
  },
  red: {
    name: 'Red',
    emoji: '🔴',
    cost: 50,
    description: 'A fiery red boulder',
  },
  yellow: {
    name: 'Yellow',
    emoji: '🟡',
    cost: 50,
    description: 'A bright yellow boulder',
  },
  shiny: {
    name: 'Shiny',
    emoji: '✨',
    cost: 200,
    description: 'A sparkling shiny boulder',
  },
  rainbow: {
    name: 'Rainbow',
    emoji: '🌈',
    cost: 500,
    description: 'A colorful rainbow boulder',
  },
  alien: {
    name: 'Alien',
    emoji: '👽',
    cost: 1000,
    description: 'An otherworldly alien boulder',
  },
  pizza: {
    name: 'Pizza',
    emoji: '🍕',
    cost: 2000,
    description: 'A delicious pizza boulder',
  },
};

interface CustomizationProps {
  isOpen: boolean;
  onClose: () => void;
  coins: number;
  ownedCustomizations: BoulderCustomization[];
  selectedCustomization: BoulderCustomization;
  onPurchase: (customization: BoulderCustomization) => void;
  onSelect: (customization: BoulderCustomization) => void;
}

const Customization: React.FC<CustomizationProps> = ({
  isOpen,
  onClose,
  coins,
  ownedCustomizations,
  selectedCustomization,
  onPurchase,
  onSelect,
}) => {
  if (!isOpen) return null;

  const isOwned = (customization: BoulderCustomization) => {
    return ownedCustomizations.includes(customization);
  };

  const canAfford = (cost: number) => {
    return coins >= cost;
  };

  return (
    <div className="customization-overlay" onClick={onClose}>
      <div className="customization-popup" onClick={(e) => e.stopPropagation()}>
        <button className="customization-close-button" onClick={onClose}>×</button>
        
        <div className="customization-header">
          <h2 className="customization-title">🎨 Boulder Customization</h2>
          <p className="customization-subtitle">Make your boulder unique!</p>
        </div>

        <div className="customization-grid">
          {Object.entries(CUSTOMIZATIONS).map(([key, item]) => {
            const customization = key as BoulderCustomization;
            const owned = isOwned(customization);
            const isSelected = selectedCustomization === customization;
            const affordable = canAfford(item.cost);

            return (
              <div
                key={key}
                className={`customization-item ${isSelected ? 'selected' : ''} ${owned ? 'owned' : ''}`}
              >
                <div className="customization-preview">
                  <div className="customization-emoji">{item.emoji}</div>
                  {isSelected && <div className="customization-checkmark">✓</div>}
                </div>
                <div className="customization-info">
                  <h3 className="customization-name">{item.name}</h3>
                  <p className="customization-description">{item.description}</p>
                  {!owned && (
                    <p className="customization-cost">{item.cost} 🪙</p>
                  )}
                </div>
                <div className="customization-actions">
                  {owned ? (
                    <button
                      className={`customization-button ${isSelected ? 'selected-button' : 'select-button'}`}
                      onClick={() => onSelect(customization)}
                    >
                      {isSelected ? 'Selected' : 'Select'}
                    </button>
                  ) : (
                    <button
                      className={`customization-button ${affordable ? 'purchase-button' : 'unaffordable-button'}`}
                      onClick={() => onPurchase(customization)}
                      disabled={!affordable}
                    >
                      Purchase ({item.cost} 🪙)
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="customization-footer">
          <p className="customization-coins">Your coins: {coins} 🪙</p>
        </div>
      </div>
    </div>
  );
};

export default Customization;

